import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Mail, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationPreferences } from '@/hooks/useNotificationPreferences';
import { useLocalizations } from '@/hooks/useLocalizations';
import { LComponents } from '@/components/i18n';
import { Button } from '@/components/ui/button';
import { db } from '@/lib/supabaseQuery';
import { shouldShowNewsletterPrompt } from '@/lib/newsletterPrompt';
import { logger } from '@/utils/logger';

interface PromptPrefs {
  mass_mail_status: string | null;
  newsletter_prompt_dismissed_at?: string | null;
  newsletter_prompt_dismiss_count?: number | null;
  newsletter_optin_requested_at?: string | null;
}

export function NewsletterPromptBanner() {
  const { user } = useAuth();
  const { preferences, isLoading } = useNotificationPreferences();
  const { lCommon } = useLocalizations();
  const queryClient = useQueryClient();
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!user || isLoading || !preferences || hidden) return null;
  const p = preferences as unknown as PromptPrefs;
  const show = shouldShowNewsletterPrompt({
    emailVerified: user.email_confirmed_at != null,
    massMailStatus: p.mass_mail_status,
    dismissedAt: p.newsletter_prompt_dismissed_at ?? null,
    dismissCount: p.newsletter_prompt_dismiss_count ?? 0,
    optinRequestedAt: p.newsletter_optin_requested_at ?? null,
  });
  if (!show) return null;

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['notification-preferences', user.id] });
  };

  const handleSubscribe = async (): Promise<void> => {
    setBusy(true);
    const { error } = await db.raw.rpc('subscribe_newsletter');
    setBusy(false);
    if (error) {
      logger.error('Newsletter subscribe failed', error);
      toast.error(lCommon('newsletter_prompt.error', { defaultValue: 'Could not subscribe. Try again.' }));
      return;
    }
    setHidden(true);
    toast.success(lCommon('newsletter_prompt.success', { defaultValue: "You're subscribed" }));
    refresh();
  };

  const handleDismiss = async (): Promise<void> => {
    setHidden(true);
    const { error } = await db.raw.rpc('dismiss_newsletter_prompt');
    if (error) logger.error('Newsletter prompt dismiss failed', error);
    refresh();
  };

  return (
    <div className="border-b border-border bg-muted/50">
      <div className="container px-4 py-2 flex flex-wrap items-center gap-3">
        <Mail className="h-4 w-4 shrink-0 text-primary" />
        <span className="flex-1 min-w-0 text-sm text-foreground">
          <LComponents id="newsletter_prompt.message" defaultValue="Get weekly tax updates from verified experts in your region." />
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <Button size="sm" className="h-8" onClick={handleSubscribe} disabled={busy}>
            {busy && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            <LComponents id="newsletter_prompt.subscribe" defaultValue="Subscribe" />
          </Button>
          <Button size="sm" variant="ghost" className="h-8" onClick={handleDismiss}>
            <LComponents id="newsletter_prompt.not_now" defaultValue="Not now" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={handleDismiss}
            aria-label={lCommon('aria.dismiss_notification', { defaultValue: 'Dismiss notification' })}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
